// ─── Animation template type system ────────────────────────────────────────
// Templates are React components that animate purely as a function of the
// current frame (Remotion's `useCurrentFrame()`). The editor treats each
// template instance as a clip on the timeline; the agent will later pick
// templates from the registry and supply `props` matching their schema.

import type React from 'react';

// Resolution we render every animation at internally. The Player scales it
// to fit the preview, and the export pipeline (when wired up) renders at
// the same size for pixel-perfect parity.
export const ANIMATION_FPS = 30;

/**
 * Common metadata shared by every prop spec.
 * - `label` is what the form shows.
 * - `agentExposed` (default: false) controls whether the agent sees this
 *   prop in its tool schema. Use this to keep the agent surface small while
 *   still giving the human user fine-grained control. Only props the agent
 *   really needs to drive (e.g. text content) should set this to true; the
 *   rest stay editable from the UI but inherit defaults when the agent
 *   creates a clip.
 */
type SpecBase = {
  label: string;
  agentExposed?: boolean;
};

/**
 * When a `text` prop is the user-facing label for a piece of copy that has
 * its own font/size/color/etc., this table links those style props by key.
 * The editor renders the linked props inside a "style" popover next to the
 * text input instead of as separate top-level rows.
 *
 * Example for a title with full styling:
 *   { font: 'titleFont', size: 'titleSize', color: 'titleColor',
 *     bold: 'titleBold', italic: 'titleItalic', align: 'titleAlign' }
 *
 * All entries are optional — only declare the aspects this text supports.
 */
export type TextStylingLinks = {
  font?: string;
  size?: string;
  color?: string;
  bold?: string;
  italic?: string;
  align?: string;
};

/**
 * Minimal description of an editable prop so the editor (and, later, the
 * agent) can build a form from a template without hard-coding it.
 * Extend with new variants as templates need them.
 */
export type AnimationPropSpec =
  | (SpecBase & {
      type: 'text';
      multiline?: boolean;
      placeholder?: string;
      /** Optional styling links — see `TextStylingLinks`. */
      styling?: TextStylingLinks;
    })
  | (SpecBase & {
      type: 'color';
      /**
       * When true, the picker exposes a "transparent" swatch (checkerboard
       * + red slash). Selecting it stores the literal CSS value
       * `'transparent'`, which most CSS color contexts handle natively.
       */
      allowTransparent?: boolean;
    })
  | (SpecBase & {
      type: 'select';
      /** Named options. The stored value is `options[i].value`. */
      options: Array<{ value: string; label: string }>;
    })
  | (SpecBase & {
      type: 'boolean';
      /** Optional one-character glyph for a compact toggle button. */
      glyph?: string;
    })
  | (SpecBase & {
      /**
       * Font family selector. Renders the editor's shared FontPicker so the
       * user gets the same font preview-as-its-own-typeface UI used for
       * text clips. The stored value is the CSS family name string.
       */
      type: 'font';
    })
  | (SpecBase & {
      type: 'number';
      min?: number;
      max?: number;
      /** Increment used by the −/+ buttons. Defaults to 1. */
      step?: number;
      /** How many decimals to display/round to. Defaults to 0. */
      decimals?: number;
      /** Optional unit suffix shown after the number (e.g. 'px', '%'). */
      unit?: string;
    })
  | (SpecBase & {
      /**
       * Image picker. The stored value is a URL string (signed URL for
       * project-asset images, or any external https URL). Templates render
       * via <Img src={value} />. Note: signed URLs may expire on reload —
       * users can re-pick the asset if that happens.
       */
      type: 'image';
    });

/**
 * Top-level grouping shown in the catalog. Templates without a category
 * fall into 'text' by default.
 */
export type AnimationCategory = 'text' | 'shape' | 'image' | 'lottie';

export interface AnimationTemplate<P extends object = Record<string, unknown>> {
  /** Stable id used by clips to reference this template. Versioned. */
  id: string;
  /** Short human label shown in pickers. */
  label: string;
  /** Optional longer description (also useful for the agent prompt). */
  description?: string;
  /** Top-level grouping in the catalog. Defaults to 'text'. */
  category?: AnimationCategory;
  /** Suggested clip duration in seconds when the user (or agent) creates one. */
  defaultDurationSec: number;
  /** Default values for every prop. Acts as both fallback and JSON schema seed. */
  defaultProps: P;
  /**
   * Per-prop UI hints used to build the edit form. Keys must match
   * `defaultProps`; props not listed here are not user-editable in the UI
   * (they can still be set programmatically, e.g. by the agent).
   */
  propsSchema?: { [K in keyof P & string]?: AnimationPropSpec };
  /**
   * Pure render function. Reads `useCurrentFrame()` and `useVideoConfig()`
   * from Remotion to know where in the timeline it is.
   */
  Component: React.ComponentType<P>;
}

/** Small helper used by VideoEditor to stamp a default animation clip. */
export type AnimationClipFields = {
  animationTemplate: string;
  animationProps: Record<string, unknown>;
};
